/**
 * Roster Pivoter Utility
 * Transforms raw schedule data into concise, staffed-only groupings
 * for the Final Roster viewer.
 */

import { compareLocations } from './locationOrder';
import { leaveDates, partOn, leaveLabel } from './leave';
export const pivotPublishedData = (schedule, config = { groupBy: 'date' }) => {
  if (!schedule) return {};

  const { shifts = [], onCallRoster = [] } = schedule;
  
  // 1. Filter for staffed shifts
  const staffedClinical = shifts.filter(s => s.employee);
  // For on-call, we include all shifts so the ledger shows gaps, 
  // but for staff view we only care about assignments
  const allOnCall = onCallRoster;
  const staffedOnCall = onCallRoster.filter(s => s.employee);

  // 2. Pivot by DATE (The Ledger View)
  if (config.groupBy === 'date') {
    const grouped = {};
    
    // Process Clinical
    staffedClinical.forEach(s => {
      const date = s.start.split('T')[0];
      if (!grouped[date]) grouped[date] = { clinical: [], onCall: [], leave: [] };
      grouped[date].clinical.push(s);
    });

    // Process On-Call (Use ALL shifts for the ledger)
    allOnCall.forEach(s => {
      const date = s.date;
      if (!grouped[date]) grouped[date] = { clinical: [], onCall: [], leave: [] };
      grouped[date].onCall.push(s);
    });

    // Process Leaves
    if (schedule.employees) {
      // Seed from the dates that already carry a staffed shift, PLUS every date
      // anyone has a leave block on. Seeding from `grouped` alone meant leave on
      // a date with no staffed shift was invisible: the Leave band rendered NIL
      // and the derived-OFF band listed people who were actually on leave.
      const allDates = new Set(Object.keys(grouped));
      schedule.employees.forEach(emp => {
        leaveDates(emp).forEach(dateStr => {
          // Validity — "2026-13-99" is the right shape but not a real
          // date, and a junk key here would surface as a junk roster column.
          if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr) &&
              !Number.isNaN(Date.parse(`${dateStr}T00:00:00`))) {
            allDates.add(dateStr);
          }
        });
      });

      allDates.forEach(dateStr => {
        schedule.employees.forEach(emp => {
          const part = partOn(emp, dateStr);
          const hasFull = part === 'full';
          const hasAM = part === 'AM';
          const hasPM = part === 'PM';

          if (hasFull || hasAM || hasPM) {
            if (!grouped[dateStr]) grouped[dateStr] = { clinical: [], onCall: [], leave: [] };
            
            let type = "Full Day";
            if (hasAM) type = "AM Only";
            else if (hasPM) type = "PM Only";

            // The human label ("Annual Leave") lives in leave_labels, keyed by date.
            const label = leaveLabel(emp, dateStr);

            grouped[dateStr].leave.push({ 
              name: emp.name, 
              type: type,
              label: label
            });
          }
        });
      });
    }

    // Sort dates
    return Object.keys(grouped).sort().reduce((acc, key) => {
      acc[key] = grouped[key];
      // Sort clinical shifts within the day: AM before PM
      acc[key].clinical.sort((a, b) => new Date(a.start) - new Date(b.start));
      return acc;
    }, {});
  }

  // 3. Pivot by STAFF (The Personal Schedule View)
  if (config.groupBy === 'staff') {
    const grouped = {};
    
    // Keyed by STAFF ID: a name is display text (two people can share a
    // spelling up to case, and a rename changes it).
    staffedClinical.forEach(s => {
      const key = s.employee.id;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push({ ...s, type: 'clinical' });
    });

    staffedOnCall.forEach(s => {
      const key = s.employee.id;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push({ ...s, type: 'oncall', start: s.date }); // Use date as start for sorting
    });

    // Sort staff names alphabetically
    return Object.keys(grouped).sort().reduce((acc, key) => {
      acc[key] = grouped[key].sort((a, b) => new Date(a.start) - new Date(b.start));
      return acc;
    }, {});
  }

  // 4. Pivot by LOCATION (The Clinic Board View)
  if (config.groupBy === 'location') {
    const grouped = {};
    
    staffedClinical.forEach(s => {
      const loc = s.location;
      if (!grouped[loc]) grouped[loc] = [];
      grouped[loc].push(s);
    });

    staffedOnCall.forEach(s => {
      const loc = 'On-Call Rotation';
      if (!grouped[loc]) grouped[loc] = [];
      grouped[loc].push({ ...s, location: loc, start: s.date });
    });

    // Clinics in the user-defined order (services/locationOrder.js); the
    // on-call pseudo-location is unknown to it and therefore lands last.
    return Object.keys(grouped).sort(compareLocations(schedule)).reduce((acc, key) => {
      acc[key] = grouped[key].sort((a, b) => new Date(a.start) - new Date(b.start));
      return acc;
    }, {});
  }

  return {};
};
